import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface ToggleOption {
  enabled: boolean;
  value?: string | number;
  routeType?: string;
  selectedOption?: string;
}

interface SectionConfig {
  [key: string]: ToggleOption;
}

interface OperatorRule {
  id: number;
  operatorName: string;
  defaultRoute: string;
  messageFormat: string;
  enableDeliveryReports: boolean;
}

interface Operator {
  id: number;
  name: string;
  prefix: string;
  priority: string;
  costPerSms: string;
  encoding: string;
  throughput: string;
}

interface GatewayConfig {
  id: number;
  name: string;
  url: string;
  apiKey: string;
  type: string;
}

interface DeliverySettings {
  enabled: boolean;
  timeBasedDelivery: {
    enabled: boolean;
    windows: Array<{
      id: number;
      startTime: string;
      endTime: string;
      days: string[];
    }>;
  };
  retryPolicy: {
    enabled: boolean;
    maxRetries: number;
    retryIntervals: number[];
  };
  messageExpiry: {
    enabled: boolean;
    expiryPeriod: number;
    expiryAction: string;
  };
  deliveryWindow: {
    enabled: boolean;
    useRecipientTimezone: boolean;
    businessHours: {
      start: string;
      end: string;
    };
  };
}

interface QualityControl {
  enabled: boolean;
  dlrTracking: {
    enabled: boolean;
    statusTypes: string[];
    updateInterval: number;
  };
  customDlrHandling: {
    enabled: boolean;
    rules: Array<{
      id: number;
      status: string;
      action: string;
      threshold?: number;
      notification?: boolean;
    }>;
  };
  tatMonitoring: {
    enabled: boolean;
    threshold: number;
    action: string;
  };
  failureAnalysis: {
    enabled: boolean;
    categories: string[];
    autoRemediation: boolean;
  };
}

interface AutoOptimization {
  enabled: boolean;
  aiBasedRouting: {
    enabled: boolean;
    learningMode: string;
    optimizationFactors: string[];
    updateFrequency: number;
  };
  autoSwitching: {
    enabled: boolean;
    performanceThreshold: number;
    failureConditions: string[];
    recoveryTime: number;
  };
  intelligentLoadDistribution: {
    enabled: boolean;
    capacityThreshold: number;
    healthMetrics: string[];
    adjustmentInterval: number;
  };
}

interface ApiIntegration {
  enabled: boolean;
  apiBasedRouting: {
    enabled: boolean;
    endpoints: Array<{
      id: number;
      name: string;
      url: string;
      method: string;
      authType: string;
      headers: Record<string, string>;
    }>;
  };
  webhookSupport: {
    enabled: boolean;
    deliveryUpdates: boolean;
    statusNotifications: boolean;
    retryAttempts: number;
    securityKey: string;
    endpoints: Array<{
      id: number;
      url: string;
      events: string[];
    }>;
  };
  gatewayAndCarrierApi: {
    enabled: boolean;
    connections: Array<{
      id: number;
      name: string;
      type: string;
      apiKey: string;
      baseUrl: string;
      status: string;
    }>;
  };
}

// First, add this interface near your other interfaces at the top of the file
interface TrafficManagement {
  enabled: boolean;
  loadBalancing: {
    enabled: boolean;
    method: string;
    threshold: number;
  };
  trafficShaping: {
    enabled: boolean;
    peakHours: Array<{
      id: number;
      startTime: string;
      endTime: string;
      maxThroughput: number;
    }>;
  };
  queueManagement: {
    enabled: boolean;
    queueSize: number;
    priorityLevels: string[];
  };
  congestionControl: {
    enabled: boolean;
    threshold: number;
    action: string;
  };
}

const RoutingParametersPage = () => {
  const navigate = useNavigate();

  const [intelligentRouting, setIntelligentRouting] = useState<SectionConfig>({
    leastCostRouting: { enabled: false },
    qualityBasedRouting: { enabled: false },
    priorityRouting: { enabled: false },
    loadBalancing: { enabled: false, value: 50 }, // percentage
  });

  const [routeConfig, setRouteConfig] = useState<SectionConfig>({
    multipleRouteSupport: { enabled: false },
    dynamicRouteSwitching: { enabled: false },
    customRouteAssignment: { enabled: false, routeType: 'Country-based' },
    routingByMessageType: { enabled: false },
  });

  const [carrierManagement, setCarrierManagement] = useState<SectionConfig>({
    carrierLookup: { enabled: false },
    networkFailover: { enabled: false },
    routeBlacklisting: { enabled: false },
    routingThrottling: { enabled: false, value: 1000 }, // messages per second
  });

  const [deliverySettings, setDeliverySettings] = useState<DeliverySettings>({
    enabled: false,
    timeBasedDelivery: {
      enabled: false,
      windows: [{
        id: 1,
        startTime: '09:00',
        endTime: '17:00',
        days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
      }]
    },
    retryPolicy: {
      enabled: false,
      maxRetries: 3,
      retryIntervals: [5, 15, 30]
    },
    messageExpiry: {
      enabled: false,
      expiryPeriod: 24,
      expiryAction: 'discard'
    },
    deliveryWindow: {
      enabled: false,
      useRecipientTimezone: false,
      businessHours: {
        start: '09:00',
        end: '17:00'
      }
    }
  });

  const [qualityControl, setQualityControl] = useState<QualityControl>({
    enabled: false,
    dlrTracking: {
      enabled: false,
      statusTypes: ['Delivered', 'Failed', 'Queued', 'Expired'],
      updateInterval: 5
    },
    customDlrHandling: {
      enabled: false,
      rules: [
        {
          id: 1,
          status: 'Failed',
          action: 'retry',
          threshold: 3,
          notification: true
        }
      ]
    },
    tatMonitoring: {
      enabled: false,
      threshold: 30,
      action: 'reroute'
    },
    failureAnalysis: {
      enabled: false,
      categories: ['Invalid Number', 'Network Error', 'Carrier Rejection', 'Expired'],
      autoRemediation: false
    }
  });

  const [autoOptimization, setAutoOptimization] = useState<AutoOptimization>({
    enabled: false,
    aiBasedRouting: {
      enabled: false,
      learningMode: 'continuous',
      optimizationFactors: ['delivery-rate', 'latency', 'cost'],
      updateFrequency: 24
    },
    autoSwitching: {
      enabled: false,
      performanceThreshold: 90,
      failureConditions: ['timeout', 'high-latency', 'delivery-failure'],
      recoveryTime: 30
    },
    intelligentLoadDistribution: {
      enabled: false,
      capacityThreshold: 80,
      healthMetrics: ['response-time', 'success-rate', 'queue-length'],
      adjustmentInterval: 5
    }
  });

  const [apiIntegration, setApiIntegration] = useState<ApiIntegration>({
    enabled: false,
    apiBasedRouting: {
      enabled: false,
      endpoints: [],
    },
    webhookSupport: {
      enabled: false,
      deliveryUpdates: false,
      statusNotifications: false,
      retryAttempts: 3,
      securityKey: '',
      endpoints: [],
    },
    gatewayAndCarrierApi: {
      enabled: false,
      connections: [],
    },
  });

  const [gatewayConfigs, setGatewayConfigs] = useState<GatewayConfig[]>([
    {
      id: 1,
      name: 'Gateway 1',
      url: '',
      apiKey: '',
      type: 'HTTP/HTTPS'
    }
  ]);

  const [connectionSettings, setConnectionSettings] = useState([
    {
      id: 1,
      name: 'Connection Setting 1',
      timeout: 30,
      retryAttempts: 3,
      poolSize: 10
    }
  ]);

  const [operators, setOperators] = useState<Operator[]>([
    { id: 1, name: 'Econet Wireless', prefix: '', priority: 'High', costPerSms: '', encoding: 'GSM-7', throughput: '' },
    { id: 2, name: 'NetOne Zimbabwe', prefix: '', priority: 'High', costPerSms: '', encoding: 'GSM-7', throughput: '' },
    { id: 3, name: 'Telecel Zimbabwe', prefix: '', priority: 'High', costPerSms: '', encoding: 'GSM-7', throughput: '' }
  ]);

  const [operatorRules, setOperatorRules] = useState<OperatorRule[]>([]);
  const [selectedOperator, setSelectedOperator] = useState<string>('');

  const [gatewayConfig, setGatewayConfig] = useState<SectionConfig>({
    configuration: { enabled: false },
    connectionSettings: { enabled: false }
  });

  const [operatorConfig, setOperatorConfig] = useState<SectionConfig>({
    settings: { enabled: false },
    specificRules: { enabled: false }
  });

  const [routeSelectionConfig, setRouteSelectionConfig] = useState<SectionConfig>({
    criteria: { enabled: false },
    optimization: { enabled: false }
  });

  const [messageTypes, setMessageTypes] = useState([
    'OTP Messages',
    'Promotional',
    'Transactional',
    'Appointments',
    'Ticket Bookings'
  ]);

  // Add this state declaration in your component
  const [trafficManagement, setTrafficManagement] = useState<TrafficManagement>({
    enabled: false,
    loadBalancing: {
      enabled: false,
      method: 'round-robin',
      threshold: 80
    },
    trafficShaping: {
      enabled: false,
      peakHours: [{
        id: 1,
        startTime: '09:00',
        endTime: '17:00',
        maxThroughput: 1000
      }]
    },
    queueManagement: {
      enabled: false,
      queueSize: 10000,
      priorityLevels: ['High', 'Medium', 'Low']
    },
    congestionControl: {
      enabled: false,
      threshold: 90,
      action: 'throttle'
    }
  });

  const handleSave = () => {
    // Implement save functionality
    console.log('Saving routing parameters...');
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-medium">SMS Routing and Delivery Parameters</h1>
        <div className="space-x-3">
          <button
            onClick={() => navigate('/superadmin/messaging')}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90 transition-colors"
          >
            Save Changes
          </button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Section 1: Gateway Parameters */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-medium mb-4">Gateway Parameters</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Gateway Configuration */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium">Gateway Configuration</h3>
                  <p className="text-sm text-gray-600">Set up SMS gateway connection parameters</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={gatewayConfig.configuration.enabled}
                    onChange={(e) => setGatewayConfig({
                      ...gatewayConfig,
                      configuration: { enabled: e.target.checked }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {gatewayConfig.configuration.enabled && (
                <div className="space-y-6">
                  {/* List of Gateway Configurations */}
                  {gatewayConfigs.map((gateway, index) => (
                    <div key={gateway.id} className="space-y-3 pb-4 border-b border-gray-200">
                      <div className="flex justify-between items-center">
                        <h4 className="text-sm font-medium text-gray-900">Gateway Configuration #{index + 1}</h4>
                        {gatewayConfigs.length > 1 && (
                          <button
                            onClick={() => {
                              const newConfigs = gatewayConfigs.filter(g => g.id !== gateway.id);
                              setGatewayConfigs(newConfigs);
                            }}
                            className="text-sm text-red-600 hover:text-red-800"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-gray-700">Gateway Name</label>
                        <input
                          type="text"
                          value={gateway.name}
                          onChange={(e) => {
                            const newConfigs = [...gatewayConfigs];
                            newConfigs[index].name = e.target.value;
                            setGatewayConfigs(newConfigs);
                          }}
                          placeholder="Enter gateway name"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-gray-700">Gateway URL</label>
                        <input
                          type="text"
                          value={gateway.url}
                          onChange={(e) => {
                            const newConfigs = [...gatewayConfigs];
                            newConfigs[index].url = e.target.value;
                            setGatewayConfigs(newConfigs);
                          }}
                          placeholder="https://gateway.example.com"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-gray-700">API Key</label>
                        <input
                          type="password"
                          value={gateway.apiKey}
                          onChange={(e) => {
                            const newConfigs = [...gatewayConfigs];
                            newConfigs[index].apiKey = e.target.value;
                            setGatewayConfigs(newConfigs);
                          }}
                          placeholder="Enter API key"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-gray-700">Gateway Type</label>
                        <select 
                          value={gateway.type}
                          onChange={(e) => {
                            const newConfigs = [...gatewayConfigs];
                            newConfigs[index].type = e.target.value;
                            setGatewayConfigs(newConfigs);
                          }}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                        >
                          <option>HTTP/HTTPS</option>
                          <option>SMPP</option>
                          <option>API</option>
                        </select>
                      </div>
                    </div>
                  ))}
                  
                  {/* Add New Gateway Button */}
                  <button
                    onClick={() => {
                      setGatewayConfigs([
                        ...gatewayConfigs,
                        {
                          id: Date.now(),
                          name: `Gateway ${gatewayConfigs.length + 1}`,
                          url: '',
                          apiKey: '',
                          type: 'HTTP/HTTPS'
                        }
                      ]);
                    }}
                    className="mt-4 w-full flex justify-center items-center px-4 py-2 border border-[#FF5722] text-[#FF5722] rounded-md hover:bg-[#FF5722] hover:text-white transition-colors"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add New Gateway Configuration
                  </button>
                </div>
              )}
            </div>

            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium">Connection Settings</h3>
                  <p className="text-sm text-gray-600">Configure gateway connection parameters</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={gatewayConfig.connectionSettings.enabled}
                    onChange={(e) => setGatewayConfig({
                      ...gatewayConfig,
                      connectionSettings: { enabled: e.target.checked }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {gatewayConfig.connectionSettings.enabled && (
                <div className="mt-3 space-y-3 border-t pt-3">
                  <div className="space-y-2">
                    {connectionSettings.map((setting, index) => (
                      <div key={setting.id} className="space-y-3 pb-4 border-b border-gray-200">
                        <div className="flex justify-between items-center">
                          <h4 className="text-sm font-medium text-gray-900">Connection Setting #{index + 1}</h4>
                          <button
                            onClick={() => {
                              const newSettings = connectionSettings.filter(s => s.id !== setting.id);
                              setConnectionSettings(newSettings);
                            }}
                            className="text-sm text-red-600 hover:text-red-800"
                          >
                            Remove
                          </button>
                        </div>

                        <div>
                          <label className="text-sm font-medium text-gray-700">Setting Name</label>
                          <input
                            type="text"
                            value={setting.name}
                            onChange={(e) => {
                              const newSettings = [...connectionSettings];
                              newSettings[index].name = e.target.value;
                              setConnectionSettings(newSettings);
                            }}
                            placeholder="Enter setting name"
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                          />
                        </div>

                        <div>
                          <label className="text-sm font-medium text-gray-700">Timeout (seconds)</label>
                          <input
                            type="number"
                            min="1"
                            value={setting.timeout}
                            onChange={(e) => {
                              const newSettings = [...connectionSettings];
                              newSettings[index].timeout = parseInt(e.target.value);
                              setConnectionSettings(newSettings);
                            }}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                          />
                        </div>

                        <div>
                          <label className="text-sm font-medium text-gray-700">Retry Attempts</label>
                          <input
                            type="number"
                            min="0"
                            value={setting.retryAttempts}
                            onChange={(e) => {
                              const newSettings = [...connectionSettings];
                              newSettings[index].retryAttempts = parseInt(e.target.value);
                              setConnectionSettings(newSettings);
                            }}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                          />
                        </div>

                        <div>
                          <label className="text-sm font-medium text-gray-700">Connection Pool Size</label>
                          <input
                            type="number"
                            min="1"
                            value={setting.poolSize}
                            onChange={(e) => {
                              const newSettings = [...connectionSettings];
                              newSettings[index].poolSize = parseInt(e.target.value);
                              setConnectionSettings(newSettings);
                            }}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                          />
                        </div>
                      </div>
                    ))}

                    {/* Add New Connection Setting Button */}
                    <button
                      onClick={() => {
                        setConnectionSettings([
                          ...connectionSettings,
                          {
                            id: Date.now(),
                            name: `Connection Setting ${connectionSettings.length + 1}`,
                            timeout: 30,
                            retryAttempts: 3,
                            poolSize: 10
                          }
                        ]);
                      }}
                      className="mt-4 w-full flex justify-center items-center px-4 py-2 border border-[#FF5722] text-[#FF5722] rounded-md hover:bg-[#FF5722] hover:text-white transition-colors"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Add New Connection Setting
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Section 2: Mobile Network Operators Parameters */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-medium mb-4">Mobile Network Operators Parameters</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium">Operator Settings</h3>
                  <p className="text-sm text-gray-600">Configure mobile network operator parameters</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={operatorConfig.settings.enabled}
                    onChange={(e) => setOperatorConfig({
                      ...operatorConfig,
                      settings: { enabled: e.target.checked }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {operatorConfig.settings.enabled && (
                <div className="mt-3 space-y-3 border-t pt-3">
                  <div className="space-y-2">
                    {operators.map((operator) => (
                      <div key={operator.id} className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex justify-between items-center mb-3">
                          <h4 className="font-medium text-sm">{operator.name}</h4>
                          <button
                            onClick={() => {
                              setOperators(operators.filter(op => op.id !== operator.id));
                            }}
                            className="text-sm text-red-600 hover:text-red-800"
                          >
                            Remove
                          </button>
                        </div>
                        
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm">Prefix</span>
                            <input
                              type="text"
                              value={operator.prefix}
                              onChange={(e) => {
                                const updatedOperators = operators.map(op =>
                                  op.id === operator.id ? { ...op, prefix: e.target.value } : op
                                );
                                setOperators(updatedOperators);
                              }}
                              className="w-24 text-sm border rounded px-2 py-1"
                              placeholder="e.g., +263"
                            />
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <span className="text-sm">Route Priority</span>
                            <select
                              value={operator.priority}
                              onChange={(e) => {
                                const updatedOperators = operators.map(op =>
                                  op.id === operator.id ? { ...op, priority: e.target.value } : op
                                );
                                setOperators(updatedOperators);
                              }}
                              className="text-sm border rounded px-2 py-1"
                            >
                              <option>High</option>
                              <option>Medium</option>
                              <option>Low</option>
                            </select>
                          </div>

                          <div className="flex items-center justify-between">
                            <span className="text-sm">Cost per SMS</span>
                            <div className="flex items-center">
                              <span className="text-sm text-gray-500 mr-2">$</span>
                              <input
                                type="number"
                                step="0.001"
                                min="0"
                                value={operator.costPerSms}
                                onChange={(e) => {
                                  const updatedOperators = operators.map(op =>
                                    op.id === operator.id ? { ...op, costPerSms: e.target.value } : op
                                  );
                                  setOperators(updatedOperators);
                                }}
                                placeholder="0.000"
                                className="w-20 text-sm border rounded px-2 py-1"
                              />
                            </div>
                          </div>

                          <div className="flex items-center justify-between">
                            <span className="text-sm">Message Encoding</span>
                            <select
                              value={operator.encoding}
                              onChange={(e) => {
                                const updatedOperators = operators.map(op =>
                                  op.id === operator.id ? { ...op, encoding: e.target.value } : op
                                );
                                setOperators(updatedOperators);
                              }}
                              className="text-sm border rounded px-2 py-1"
                            >
                              <option>GSM-7</option>
                              <option>Unicode (UCS-2)</option>
                            </select>
                          </div>

                          <div className="flex items-center justify-between">
                            <span className="text-sm">Throughput Limit</span>
                            <div className="flex items-center">
                              <input
                                type="number"
                                min="1"
                                value={operator.throughput}
                                onChange={(e) => {
                                  const updatedOperators = operators.map(op =>
                                    op.id === operator.id ? { ...op, throughput: e.target.value } : op
                                  );
                                  setOperators(updatedOperators);
                                }}
                                placeholder="Max msgs/sec"
                                className="w-28 text-sm border rounded px-2 py-1"
                              />
                              <span className="text-xs text-gray-500 ml-2">msgs/sec</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Add New Operator Section */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex items-center">
                        <input
                          type="text"
                          placeholder="Enter new operator name"
                          className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                          id="newOperatorName"
                        />
                        <button
                          onClick={() => {
                            const newOperatorName = (document.getElementById('newOperatorName') as HTMLInputElement).value;
                            if (newOperatorName.trim()) {
                              setOperators([
                                ...operators,
                                {
                                  id: Date.now(),
                                  name: newOperatorName,
                                  prefix: '',
                                  priority: 'High',
                                  costPerSms: '',
                                  encoding: 'GSM-7',
                                  throughput: ''
                                }
                              ]);
                              (document.getElementById('newOperatorName') as HTMLInputElement).value = '';
                            }
                          }}
                          className="ml-2 px-4 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#FF5722]/90 transition-colors"
                        >
                          Add Operator
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium">Operator Specific Rules</h3>
                  <p className="text-sm text-gray-600">Set specific routing rules for each operator</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={operatorConfig.specificRules.enabled}
                    onChange={(e) => setOperatorConfig({
                      ...operatorConfig,
                      specificRules: { enabled: e.target.checked }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {operatorConfig.specificRules.enabled && (
                <div className="mt-3 space-y-3 border-t pt-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Select Operator</label>
                    <div className="flex gap-2 mt-1">
                      <select 
                        className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                        value={selectedOperator}
                        onChange={(e) => setSelectedOperator(e.target.value)}
                      >
                        <option value="">Select an operator</option>
                        {operators.map((op) => (
                          <option key={op.id} value={op.name}>{op.name}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => {
                          if (selectedOperator && !operatorRules.find(rule => rule.operatorName === selectedOperator)) {
                            setOperatorRules([
                              ...operatorRules,
                              {
                                id: Date.now(),
                                operatorName: selectedOperator,
                                defaultRoute: 'Primary Gateway',
                                messageFormat: 'Plain Text',
                                enableDeliveryReports: false
                              }
                            ]);
                          }
                        }}
                        className="px-4 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#FF5722]/90 transition-colors"
                      >
                        Add Rule
                      </button>
                    </div>
                  </div>

                  {/* List of Operator Rules */}
                  <div className="space-y-4">
                    {operatorRules.map((rule) => (
                      <div key={rule.id} className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex justify-between items-center mb-3">
                          <h4 className="font-medium text-sm">{rule.operatorName}</h4>
                          <button
                            onClick={() => {
                              setOperatorRules(operatorRules.filter(r => r.id !== rule.id));
                            }}
                            className="text-sm text-red-600 hover:text-red-800"
                          >
                            Remove
                          </button>
                        </div>

                        <div className="space-y-3">
                          <div>
                            <label className="text-sm font-medium text-gray-700">Default Route</label>
                            <select
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                              value={rule.defaultRoute}
                              onChange={(e) => {
                                setOperatorRules(operatorRules.map(r =>
                                  r.id === rule.id ? { ...r, defaultRoute: e.target.value } : r
                                ));
                              }}
                            >
                              <option>Primary Gateway</option>
                              <option>Secondary Gateway</option>
                              <option>Failover Gateway</option>
                            </select>
                          </div>

                          <div>
                            <label className="text-sm font-medium text-gray-700">Message Format</label>
                            <select
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                              value={rule.messageFormat}
                              onChange={(e) => {
                                setOperatorRules(operatorRules.map(r =>
                                  r.id === rule.id ? { ...r, messageFormat: e.target.value } : r
                                ));
                              }}
                            >
                              <option>Plain Text</option>
                              <option>Unicode</option>
                              <option>Flash SMS</option>
                            </select>
                          </div>

                          <div>
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={rule.enableDeliveryReports}
                                onChange={(e) => {
                                  setOperatorRules(operatorRules.map(r =>
                                    r.id === rule.id ? { ...r, enableDeliveryReports: e.target.checked } : r
                                  ));
                                }}
                                className="rounded text-[#FF5722] mr-2"
                              />
                              <span className="text-sm">Enable Delivery Reports</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Add New Operator Section */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center">
                      <input
                        type="text"
                        placeholder="Enter new operator name"
                        className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                        id="newOperatorNameForRules"
                      />
                      <button
                        onClick={() => {
                          const newOperatorName = (document.getElementById('newOperatorNameForRules') as HTMLInputElement).value;
                          if (newOperatorName.trim() && !operators.find(op => op.name === newOperatorName)) {
                            // Add to operators list
                            const newOperator = {
                              id: Date.now(),
                              name: newOperatorName,
                              prefix: '',
                              priority: 'High',
                              costPerSms: '',
                              encoding: 'GSM-7',
                              throughput: ''
                            };
                            setOperators([...operators, newOperator]);
                            
                            // Add default rule
                            setOperatorRules([
                              ...operatorRules,
                              {
                                id: Date.now() + 1,
                                operatorName: newOperatorName,
                                defaultRoute: 'Primary Gateway',
                                messageFormat: 'Plain Text',
                                enableDeliveryReports: false
                              }
                            ]);
                            
                            (document.getElementById('newOperatorNameForRules') as HTMLInputElement).value = '';
                          }
                        }}
                        className="ml-2 px-4 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#FF5722]/90 transition-colors"
                      >
                        Add Operator
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Section 3: Routing Parameters */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-medium mb-4">Routing Parameters</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium">Route Selection Criteria</h3>
                  <p className="text-sm text-gray-600">Define primary routing parameters</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={routeSelectionConfig.criteria.enabled}
                    onChange={(e) => setRouteSelectionConfig({
                      ...routeSelectionConfig,
                      criteria: { enabled: e.target.checked }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {routeSelectionConfig.criteria.enabled && (
                <div className="mt-3 space-y-3 border-t pt-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Primary Selection Method</label>
                    <select className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm">
                      <option>Cost Based</option>
                      <option>Quality Based</option>
                      <option>Hybrid</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Fallback Strategy</label>
                    <select className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm">
                      <option>Next Best Route</option>
                      <option>Least Cost Available</option>
                      <option>Highest Quality Available</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium">Route Optimization</h3>
                  <p className="text-sm text-gray-600">Configure route optimization settings</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={routeSelectionConfig.optimization.enabled}
                    onChange={(e) => setRouteSelectionConfig({
                      ...routeSelectionConfig,
                      optimization: { enabled: e.target.checked }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {routeSelectionConfig.optimization.enabled && (
                <div className="mt-3 space-y-3 border-t pt-3">
                  <div>
                    <label className="flex items-center">
                      <input type="checkbox" className="rounded text-[#FF5722] mr-2" />
                      <span className="text-sm">Auto-optimize routes</span>
                    </label>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Optimization Interval</label>
                    <select className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm">
                      <option>Every Hour</option>
                      <option>Every 6 Hours</option>
                      <option>Every 12 Hours</option>
                      <option>Daily</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Performance Metrics</label>
                    <div className="space-y-2 mt-2">
                      <label className="flex items-center">
                        <input type="checkbox" className="rounded text-[#FF5722] mr-2" />
                        <span className="text-sm">Delivery Rate</span>
                      </label>
                      <label className="flex items-center">
                        <input type="checkbox" className="rounded text-[#FF5722] mr-2" />
                        <span className="text-sm">Latency</span>
                      </label>
                      <label className="flex items-center">
                        <input type="checkbox" className="rounded text-[#FF5722] mr-2" />
                        <span className="text-sm">Cost Efficiency</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Section 4: Intelligent Routing */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-medium mb-4">Intelligent Routing</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Least Cost Routing */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium">Least Cost Routing</h3>
                  <p className="text-sm text-gray-600">Automatically select the most cost-effective route</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={intelligentRouting.leastCostRouting.enabled}
                    onChange={(e) => setIntelligentRouting({
                      ...intelligentRouting,
                      leastCostRouting: { enabled: e.target.checked }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {intelligentRouting.leastCostRouting.enabled && (
                <div className="mt-3 space-y-3 border-t pt-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Cost Threshold (per message)</label>
                    <select className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm">
                      <option>$0.001 - Lowest cost only</option>
                      <option>$0.002 - Balance cost and quality</option>
                      <option>$0.005 - Premium routes</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Quality-Based Routing */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium">Quality-Based Routing</h3>
                  <p className="text-sm text-gray-600">Route based on performance indicators</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={intelligentRouting.qualityBasedRouting.enabled}
                    onChange={(e) => setIntelligentRouting({
                      ...intelligentRouting,
                      qualityBasedRouting: { enabled: e.target.checked }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {intelligentRouting.qualityBasedRouting.enabled && (
                <div className="mt-3 space-y-3 border-t pt-3">
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input type="checkbox" className="rounded text-[#FF5722] mr-2" />
                      <span className="text-sm">Delivery Speed</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="rounded text-[#FF5722] mr-2" />
                      <span className="text-sm">DLR Success Rate</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="rounded text-[#FF5722] mr-2" />
                      <span className="text-sm">Network Latency</span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Priority Routing */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium">Priority Routing</h3>
                  <p className="text-sm text-gray-600">Assign priority for time-sensitive messages</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={intelligentRouting.priorityRouting.enabled}
                    onChange={(e) => setIntelligentRouting({
                      ...intelligentRouting,
                      priorityRouting: { enabled: e.target.checked }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {intelligentRouting.priorityRouting.enabled && (
                <div className="mt-3 space-y-3 border-t pt-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Priority Levels</label>
                    <select className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm">
                      <option>High - Critical Messages</option>
                      <option>Medium - Important Updates</option>
                      <option>Low - Regular Messages</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Load Balancing */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium">Load Balancing</h3>
                  <p className="text-sm text-gray-600">Distribute traffic across multiple routes</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={intelligentRouting.loadBalancing.enabled}
                    onChange={(e) => setIntelligentRouting({
                      ...intelligentRouting,
                      loadBalancing: { enabled: e.target.checked, value: intelligentRouting.loadBalancing.value }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {intelligentRouting.loadBalancing.enabled && (
                <div className="mt-3 space-y-3 border-t pt-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Distribution Method</label>
                    <select className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm">
                      <option>Round Robin</option>
                      <option>Weighted Distribution</option>
                      <option>Dynamic Allocation</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Load Threshold (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={intelligentRouting.loadBalancing.value}
                      onChange={(e) => setIntelligentRouting({
                        ...intelligentRouting,
                        loadBalancing: { enabled: true, value: parseInt(e.target.value) }
                      })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Section 5: Route Configuration */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-medium mb-4">Route Configuration</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Multiple Route Support */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium">Multiple Route Support</h3>
                  <p className="text-sm text-gray-600">Configure multiple SMS gateways and providers</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={routeConfig.multipleRouteSupport.enabled}
                    onChange={(e) => setRouteConfig({
                      ...routeConfig,
                      multipleRouteSupport: { enabled: e.target.checked }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {routeConfig.multipleRouteSupport.enabled && (
                <div className="mt-3 space-y-3 border-t pt-3">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span className="text-sm font-medium">Gateway 1</span>
                      <select className="text-sm border rounded px-2 py-1">
                        <option>Primary</option>
                        <option>Secondary</option>
                        <option>Backup</option>
                      </select>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span className="text-sm font-medium">Gateway 2</span>
                      <select className="text-sm border rounded px-2 py-1">
                        <option>Primary</option>
                        <option>Secondary</option>
                        <option>Backup</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Dynamic Route Switching */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium">Dynamic Route Switching</h3>
                  <p className="text-sm text-gray-600">Auto-switch to backup routes on failure</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={routeConfig.dynamicRouteSwitching.enabled}
                    onChange={(e) => setRouteConfig({
                      ...routeConfig,
                      dynamicRouteSwitching: { enabled: e.target.checked }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {routeConfig.dynamicRouteSwitching.enabled && (
                <div className="mt-3 space-y-3 border-t pt-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Failover Threshold</label>
                    <select className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm">
                      <option>3 failed attempts</option>
                      <option>5 failed attempts</option>
                      <option>10 failed attempts</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Recovery Time</label>
                    <select className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm">
                      <option>5 minutes</option>
                      <option>15 minutes</option>
                      <option>30 minutes</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Custom Route Assignment */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium">Custom Route Assignment</h3>
                  <p className="text-sm text-gray-600">Assign specific routes for countries and carriers</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={routeConfig.customRouteAssignment.enabled}
                    onChange={(e) => setRouteConfig({
                      ...routeConfig,
                      customRouteAssignment: { enabled: e.target.checked }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {routeConfig.customRouteAssignment.enabled && (
                <div className="mt-3 space-y-3 border-t pt-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Route Type</label>
                    <select 
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                      value={routeConfig.customRouteAssignment.routeType}
                      onChange={(e) => {
                        setRouteConfig({
                          ...routeConfig,
                          customRouteAssignment: { 
                            ...routeConfig.customRouteAssignment,
                            routeType: e.target.value 
                          }
                        });
                      }}
                    >
                      <option value="Country-based">Country-based</option>
                      <option value="Carrier-based">Carrier-based</option>
                      <option value="Message Type-based">Message Type-based</option>
                    </select>
                  </div>
                  
                  {/* Show carriers when Carrier-based is selected */}
                  {routeConfig.customRouteAssignment.routeType === 'Carrier-based' && (
                    <div className="mt-4">
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Select Carriers</label>
                      <div className="space-y-2 mt-2">
                        {operators.map((operator) => (
                          <div 
                            key={operator.id} 
                            className="flex items-center justify-between p-2 bg-gray-50 rounded"
                          >
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                id={`carrier-${operator.id}`}
                                className="rounded text-[#FF5722] mr-2 focus:ring-[#FF5722]"
                              />
                              <label htmlFor={`carrier-${operator.id}`} className="text-sm">
                                {operator.name}
                              </label>
                            </div>
                            <select className="text-sm border rounded px-2 py-1">
                              <option>Gateway 1</option>
                              <option>Gateway 2</option>
                              <option>Gateway 3</option>
                            </select>
                          </div>
                        ))}
                      </div>
                      
                      {operators.length === 0 && (
                        <div className="text-sm text-gray-500 text-center py-4">
                          No operators configured. Please add operators in the Operator Settings section.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Show existing region options for Country-based */}
                  {routeConfig.customRouteAssignment.routeType === 'Country-based' && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Route Assignment</label>
                      <div className="mt-2 space-y-2">
                        <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="text-sm">Africa Region</span>
                          <select className="text-sm border rounded px-2 py-1">
                            <option>Gateway 1</option>
                            <option>Gateway 2</option>
                          </select>
                        </div>
                        <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="text-sm">Asia Region</span>
                          <select className="text-sm border rounded px-2 py-1">
                            <option>Gateway 1</option>
                            <option>Gateway 2</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Show message types when Message Type-based is selected */}
                  {routeConfig.customRouteAssignment.routeType === 'Message Type-based' && (
                    <div className="mt-4">
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Select Message Types</label>
                      <div className="space-y-2 mt-2">
                        {messageTypes.map((messageType) => (
                          <div key={messageType} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                id={`message-type-${messageType}`}
                                className="rounded text-[#FF5722] mr-2 focus:ring-[#FF5722]"
                              />
                              <label htmlFor={`message-type-${messageType}`} className="text-sm">{messageType}</label>
                            </div>
                            <select className="text-sm border rounded px-2 py-1">
                              <option>Gateway 1</option>
                              <option>Gateway 2</option>
                              <option>Gateway 3</option>
                            </select>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Routing by Message Type */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium">Routing by Message Type</h3>
                  <p className="text-sm text-gray-600">Customize routes for different message types</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={routeConfig.routingByMessageType.enabled}
                    onChange={(e) => setRouteConfig({
                      ...routeConfig,
                      routingByMessageType: { enabled: e.target.checked }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {routeConfig.routingByMessageType.enabled && (
                <div className="mt-3 space-y-3 border-t pt-3">
                  <div className="space-y-2">
                    {/* Existing message types */}
                    <div className="space-y-2">
                      {messageTypes.map((type) => (
                        <div key={type} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="text-sm">{type}</span>
                          <select className="text-sm border rounded px-2 py-1">
                            <option>High Priority Route</option>
                            <option>Premium Route</option>
                            <option>Standard Route</option>
                            <option>Bulk Route</option>
                          </select>
                        </div>
                      ))}
                    </div>

                    {/* Add New Message Type Section */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex items-center">
                        <input
                          type="text"
                          placeholder="Enter new message type"
                          className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                          id="newMessageType"
                        />
                        <button
                          onClick={() => {
                            const newType = (document.getElementById('newMessageType') as HTMLInputElement).value;
                            if (newType.trim() && !messageTypes.includes(newType)) {
                              setMessageTypes([...messageTypes, newType]);
                              (document.getElementById('newMessageType') as HTMLInputElement).value = '';
                            }
                          }}
                          className="ml-2 px-4 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#FF5722]/90 transition-colors text-sm"
                        >
                          Add Type
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Section 6: Carrier and Network Management */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-medium mb-4">Carrier and Network Management</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Carrier Lookup */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium">Carrier Lookup</h3>
                  <p className="text-sm text-gray-600">Identify recipient's carrier and network before routing</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={carrierManagement.carrierLookup.enabled}
                    onChange={(e) => setCarrierManagement({
                      ...carrierManagement,
                      carrierLookup: { enabled: e.target.checked }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {carrierManagement.carrierLookup.enabled && (
                <div className="mt-3 space-y-3 border-t pt-3">
                  <div className="space-y-2">
                    {['Econet Wireless', 'NetOne Zimbabwe', 'Telecel Zimbabwe'].map((carrier) => (
                      <div key={carrier} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id={`lookup-${carrier}`}
                            className="rounded text-[#FF5722] mr-2 focus:ring-[#FF5722]"
                          />
                          <label htmlFor={`lookup-${carrier}`} className="text-sm">{carrier}</label>
                        </div>
                      </div>
                    ))}
                    <div className="mt-3">
                      <label className="text-sm font-medium text-gray-700">Add New Carrier</label>
                      <div className="flex mt-1 space-x-2">
                        <input
                          type="text"
                          placeholder="Enter carrier name"
                          className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                        />
                        <button className="px-3 py-1 bg-[#FF5722] text-white rounded-md hover:bg-[#FF5722]/90 text-sm">
                          Add
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Network Failover */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium">Network Failover</h3>
                  <p className="text-sm text-gray-600">Auto-reroute through alternative gateways on network issues</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={carrierManagement.networkFailover.enabled}
                    onChange={(e) => setCarrierManagement({
                      ...carrierManagement,
                      networkFailover: { enabled: e.target.checked }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {carrierManagement.networkFailover.enabled && (
                <div className="mt-3 space-y-3 border-t pt-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Failover Conditions</label>
                    <div className="space-y-2 mt-2">
                      <label className="flex items-center">
                        <input type="checkbox" className="rounded text-[#FF5722] mr-2" />
                        <span className="text-sm">Network Timeout</span>
                      </label>
                      <label className="flex items-center">
                        <input type="checkbox" className="rounded text-[#FF5722] mr-2" />
                        <span className="text-sm">Connection Error</span>
                      </label>
                      <label className="flex items-center">
                        <input type="checkbox" className="rounded text-[#FF5722] mr-2" />
                        <span className="text-sm">High Latency</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Route Blacklisting */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium">Route Blacklisting</h3>
                  <p className="text-sm text-gray-600">Block routes with poor performance or high costs</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={carrierManagement.routeBlacklisting.enabled}
                    onChange={(e) => setCarrierManagement({
                      ...carrierManagement,
                      routeBlacklisting: { enabled: e.target.checked }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {carrierManagement.routeBlacklisting.enabled && (
                <div className="mt-3 space-y-3 border-t pt-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Blacklist Criteria</label>
                    <div className="space-y-2 mt-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Cost Threshold</span>
                        <input
                          type="number"
                          placeholder="$/message"
                          className="w-24 text-sm border rounded px-2 py-1"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Failure Rate</span>
                        <input
                          type="number"
                          placeholder="%"
                          className="w-24 text-sm border rounded px-2 py-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Routing Throttling */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium">Routing Throttling</h3>
                  <p className="text-sm text-gray-600">Control message flow to avoid network congestion</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={carrierManagement.routingThrottling.enabled}
                    onChange={(e) => setCarrierManagement({
                      ...carrierManagement,
                      routingThrottling: { enabled: e.target.checked, value: carrierManagement.routingThrottling.value }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {carrierManagement.routingThrottling.enabled && (
                <div className="mt-3 space-y-3 border-t pt-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Messages per Second</label>
                    <input
                      type="number"
                      min="1"
                      value={carrierManagement.routingThrottling.value}
                      onChange={(e) => setCarrierManagement({
                        ...carrierManagement,
                        routingThrottling: { enabled: true, value: parseInt(e.target.value) }
                      })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                    />
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Apply Throttling To</label>
                      <select 
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                        onChange={(e) => {
                          setCarrierManagement({
                            ...carrierManagement,
                            routingThrottling: { 
                              ...carrierManagement.routingThrottling,
                              value: carrierManagement.routingThrottling.value,
                              enabled: true,
                              selectedOption: e.target.value 
                            }
                          });
                        }}
                      >
                        <option value="all">All Carriers</option>
                        <option value="selected">Selected Carriers Only</option>
                        <option value="specific">Specific Routes</option>
                      </select>
                    </div>

                    {/* Show carriers when Selected Carriers Only is chosen */}
                    {carrierManagement.routingThrottling.selectedOption === 'selected' && (
                      <div className="mt-3 space-y-2 border-t pt-3">
                        <label className="text-sm font-medium text-gray-700 block mb-2">Select Carriers to Throttle</label>
                        {['Econet Wireless', 'NetOne Zimbabwe', 'Telecel Zimbabwe'].map((carrier) => (
                          <div key={carrier} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                id={`throttle-${carrier}`}
                                className="rounded text-[#FF5722] mr-2 focus:ring-[#FF5722]"
                              />
                              <label htmlFor={`throttle-${carrier}`} className="text-sm">{carrier}</label>
                            </div>
                            <input
                              type="number"
                              placeholder="msgs/sec"
                              className="w-24 text-sm border rounded px-2 py-1"
                              min="1"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Section 7: Delivery Settings */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-medium mb-4">Delivery Settings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Time-Based Delivery */}
            <div className="border-b pb-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium">Time-Based Delivery</h4>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={deliverySettings.timeBasedDelivery.enabled}
                    onChange={(e) => setDeliverySettings({
                      ...deliverySettings,
                      timeBasedDelivery: {
                        ...deliverySettings.timeBasedDelivery,
                        enabled: e.target.checked
                      }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {deliverySettings.timeBasedDelivery.enabled && (
                <div className="space-y-3">
                  {deliverySettings.timeBasedDelivery.windows.map((window, index) => (
                    <div key={window.id} className="p-3 bg-gray-50 rounded">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Start Time</label>
                          <input
                            type="time"
                            value={window.startTime}
                            onChange={(e) => {
                              const newWindows = [...deliverySettings.timeBasedDelivery.windows];
                              newWindows[index].startTime = e.target.value;
                              setDeliverySettings({
                                ...deliverySettings,
                                timeBasedDelivery: {
                                  ...deliverySettings.timeBasedDelivery,
                                  windows: newWindows
                                }
                              });
                            }}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">End Time</label>
                          <input
                            type="time"
                            value={window.endTime}
                            onChange={(e) => {
                              const newWindows = [...deliverySettings.timeBasedDelivery.windows];
                              newWindows[index].endTime = e.target.value;
                              setDeliverySettings({
                                ...deliverySettings,
                                timeBasedDelivery: {
                                  ...deliverySettings.timeBasedDelivery,
                                  windows: newWindows
                                }
                              });
                            }}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                          />
                        </div>
                      </div>
                      <div className="mt-3">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Days</label>
                        <div className="flex flex-wrap gap-2">
                          {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                            <label key={day} className="inline-flex items-center">
                              <input
                                type="checkbox"
                                checked={window.days.includes(day)}
                                onChange={(e) => {
                                  const newWindows = [...deliverySettings.timeBasedDelivery.windows];
                                  if (e.target.checked) {
                                    newWindows[index].days.push(day);
                                  } else {
                                    newWindows[index].days = newWindows[index].days.filter(d => d !== day);
                                  }
                                  setDeliverySettings({
                                    ...deliverySettings,
                                    timeBasedDelivery: {
                                      ...deliverySettings.timeBasedDelivery,
                                      windows: newWindows
                                    }
                                  });
                                }}
                                className="rounded border-gray-300 text-[#FF5722] focus:ring-[#FF5722]"
                              />
                              <span className="ml-2 text-sm text-gray-700">{day.slice(0, 3)}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      setDeliverySettings({
                        ...deliverySettings,
                        timeBasedDelivery: {
                          ...deliverySettings.timeBasedDelivery,
                          windows: [
                            ...deliverySettings.timeBasedDelivery.windows,
                            {
                              id: Date.now(),
                              startTime: '09:00',
                              endTime: '17:00',
                              days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
                            }
                          ]
                        }
                      });
                    }}
                    className="mt-2 w-full flex justify-center items-center px-4 py-2 border border-[#FF5722] text-[#FF5722] rounded-md hover:bg-[#FF5722] hover:text-white transition-colors"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Delivery Window
                  </button>
                </div>
              )}
            </div>

            {/* Retry Policy */}
            <div className="border-b pb-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium">Retry Policy</h4>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={deliverySettings.retryPolicy.enabled}
                    onChange={(e) => setDeliverySettings({
                      ...deliverySettings,
                      retryPolicy: {
                        ...deliverySettings.retryPolicy,
                        enabled: e.target.checked
                      }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {deliverySettings.retryPolicy.enabled && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Maximum Retries</label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={deliverySettings.retryPolicy.maxRetries}
                      onChange={(e) => setDeliverySettings({
                        ...deliverySettings,
                        retryPolicy: {
                          ...deliverySettings.retryPolicy,
                          maxRetries: parseInt(e.target.value)
                        }
                      })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Retry Intervals (minutes)</label>
                    <div className="flex gap-2 mt-1">
                      {deliverySettings.retryPolicy.retryIntervals.map((interval, index) => (
                        <input
                          key={index}
                          type="number"
                          min="1"
                          value={interval}
                          onChange={(e) => {
                            const newIntervals = [...deliverySettings.retryPolicy.retryIntervals];
                            newIntervals[index] = parseInt(e.target.value);
                            setDeliverySettings({
                              ...deliverySettings,
                              retryPolicy: {
                                ...deliverySettings.retryPolicy,
                                retryIntervals: newIntervals
                              }
                            });
                          }}
                          className="block w-20 rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Message Expiry */}
            <div className="border-b pb-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium">Message Expiry</h4>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={deliverySettings.messageExpiry.enabled}
                    onChange={(e) => setDeliverySettings({
                      ...deliverySettings,
                      messageExpiry: {
                        ...deliverySettings.messageExpiry,
                        enabled: e.target.checked
                      }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {deliverySettings.messageExpiry.enabled && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Expiry Period (hours)</label>
                    <input
                      type="number"
                      min="1"
                      value={deliverySettings.messageExpiry.expiryPeriod}
                      onChange={(e) => setDeliverySettings({
                        ...deliverySettings,
                        messageExpiry: {
                          ...deliverySettings.messageExpiry,
                          expiryPeriod: parseInt(e.target.value)
                        }
                      })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Expiry Action</label>
                    <select
                      value={deliverySettings.messageExpiry.expiryAction}
                      onChange={(e) => setDeliverySettings({
                        ...deliverySettings,
                        messageExpiry: {
                          ...deliverySettings.messageExpiry,
                          expiryAction: e.target.value
                        }
                      })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                    >
                      <option value="discard">Discard Message</option>
                      <option value="archive">Archive Message</option>
                      <option value="retry">Force Retry</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Delivery Window Control */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium">Delivery Window Control</h4>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={deliverySettings.deliveryWindow.enabled}
                    onChange={(e) => setDeliverySettings({
                      ...deliverySettings,
                      deliveryWindow: {
                        ...deliverySettings.deliveryWindow,
                        enabled: e.target.checked
                      }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {deliverySettings.deliveryWindow.enabled && (
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={deliverySettings.deliveryWindow.useRecipientTimezone}
                      onChange={(e) => setDeliverySettings({
                        ...deliverySettings,
                        deliveryWindow: {
                          ...deliverySettings.deliveryWindow,
                          useRecipientTimezone: e.target.checked
                        }
                      })}
                      className="rounded border-gray-300 text-[#FF5722] focus:ring-[#FF5722]"
                    />
                    <label className="ml-2 text-sm text-gray-700">Use recipient's timezone</label>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Business Hours Start</label>
                      <input
                        type="time"
                        value={deliverySettings.deliveryWindow.businessHours.start}
                        onChange={(e) => setDeliverySettings({
                          ...deliverySettings,
                          deliveryWindow: {
                            ...deliverySettings.deliveryWindow,
                            businessHours: {
                              ...deliverySettings.deliveryWindow.businessHours,
                              start: e.target.value
                            }
                          }
                        })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Business Hours End</label>
                      <input
                        type="time"
                        value={deliverySettings.deliveryWindow.businessHours.end}
                        onChange={(e) => setDeliverySettings({
                          ...deliverySettings,
                          deliveryWindow: {
                            ...deliverySettings.deliveryWindow,
                            businessHours: {
                              ...deliverySettings.deliveryWindow.businessHours,
                              end: e.target.value
                            }
                          }
                        })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Section 8: Delivery Quality Control */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-medium mb-4">Delivery Quality Control</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* DLR Tracking */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium">DLR Tracking</h3>
                  <p className="text-sm text-gray-600">Monitor message delivery status in real-time</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={qualityControl.dlrTracking.enabled}
                    onChange={(e) => setQualityControl({
                      ...qualityControl,
                      dlrTracking: {
                        ...qualityControl.dlrTracking,
                        enabled: e.target.checked
                      }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {qualityControl.dlrTracking.enabled && (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Status Types to Track</label>
                    <div className="mt-2 space-y-2">
                      {qualityControl.dlrTracking.statusTypes.map((status) => (
                        <label key={status} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={true}
                            className="rounded border-gray-300 text-[#FF5722] focus:ring-[#FF5722]"
                          />
                          <span className="ml-2 text-sm text-gray-700">{status}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Update Interval (minutes)</label>
                    <input
                      type="number"
                      min="1"
                      value={qualityControl.dlrTracking.updateInterval}
                      onChange={(e) => setQualityControl({
                        ...qualityControl,
                        dlrTracking: {
                          ...qualityControl.dlrTracking,
                          updateInterval: parseInt(e.target.value)
                        }
                      })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Custom DLR Handling */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium">Custom DLR Handling</h3>
                  <p className="text-sm text-gray-600">Configure actions for different delivery statuses</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={qualityControl.customDlrHandling.enabled}
                    onChange={(e) => setQualityControl({
                      ...qualityControl,
                      customDlrHandling: {
                        ...qualityControl.customDlrHandling,
                        enabled: e.target.checked
                      }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {qualityControl.customDlrHandling.enabled && (
                <div className="space-y-4">
                  {qualityControl.customDlrHandling.rules.map((rule, index) => (
                    <div key={rule.id} className="p-3 bg-gray-50 rounded">
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm font-medium text-gray-700">Status</label>
                          <select
                            value={rule.status}
                            onChange={(e) => {
                              const newRules = [...qualityControl.customDlrHandling.rules];
                              newRules[index].status = e.target.value;
                              setQualityControl({
                                ...qualityControl,
                                customDlrHandling: {
                                  ...qualityControl.customDlrHandling,
                                  rules: newRules
                                }
                              });
                            }}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                          >
                            <option>Failed</option>
                            <option>Expired</option>
                            <option>Rejected</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-700">Action</label>
                          <select
                            value={rule.action}
                            onChange={(e) => {
                              const newRules = [...qualityControl.customDlrHandling.rules];
                              newRules[index].action = e.target.value;
                              setQualityControl({
                                ...qualityControl,
                                customDlrHandling: {
                                  ...qualityControl.customDlrHandling,
                                  rules: newRules
                                }
                              });
                            }}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                          >
                            <option value="retry">Retry Delivery</option>
                            <option value="reroute">Reroute Message</option>
                            <option value="alert">Send Alert</option>
                            <option value="archive">Archive Message</option>
                          </select>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={rule.notification}
                            onChange={(e) => {
                              const newRules = [...qualityControl.customDlrHandling.rules];
                              newRules[index].notification = e.target.checked;
                              setQualityControl({
                                ...qualityControl,
                                customDlrHandling: {
                                  ...qualityControl.customDlrHandling,
                                  rules: newRules
                                }
                              });
                            }}
                            className="rounded border-gray-300 text-[#FF5722] focus:ring-[#FF5722]"
                          />
                          <label className="ml-2 text-sm text-gray-700">Send Notification</label>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      setQualityControl({
                        ...qualityControl,
                        customDlrHandling: {
                          ...qualityControl.customDlrHandling,
                          rules: [
                            ...qualityControl.customDlrHandling.rules,
                            {
                              id: Date.now(),
                              status: 'Failed',
                              action: 'retry',
                              threshold: 3,
                              notification: true
                            }
                          ]
                        }
                      });
                    }}
                    className="w-full flex justify-center items-center px-4 py-2 border border-[#FF5722] text-[#FF5722] rounded-md hover:bg-[#FF5722] hover:text-white transition-colors"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Rule
                  </button>
                </div>
              )}
            </div>

            {/* TAT Monitoring */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium">TAT Monitoring</h3>
                  <p className="text-sm text-gray-600">Monitor message delivery turnaround time</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={qualityControl.tatMonitoring.enabled}
                    onChange={(e) => setQualityControl({
                      ...qualityControl,
                      tatMonitoring: {
                        ...qualityControl.tatMonitoring,
                        enabled: e.target.checked
                      }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {qualityControl.tatMonitoring.enabled && (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">TAT Threshold (seconds)</label>
                    <input
                      type="number"
                      min="1"
                      value={qualityControl.tatMonitoring.threshold}
                      onChange={(e) => setQualityControl({
                        ...qualityControl,
                        tatMonitoring: {
                          ...qualityControl.tatMonitoring,
                          threshold: parseInt(e.target.value)
                        }
                      })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Action on Threshold Exceeded</label>
                    <select
                      value={qualityControl.tatMonitoring.action}
                      onChange={(e) => setQualityControl({
                        ...qualityControl,
                        tatMonitoring: {
                          ...qualityControl.tatMonitoring,
                          action: e.target.value
                        }
                      })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                    >
                      <option value="reroute">Reroute Message</option>
                      <option value="alert">Send Alert</option>
                      <option value="escalate">Escalate to Support</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Failed Delivery Analysis */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium">Failed Delivery Analysis</h3>
                  <p className="text-sm text-gray-600">Analyze and categorize delivery failures</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={qualityControl.failureAnalysis.enabled}
                    onChange={(e) => setQualityControl({
                      ...qualityControl,
                      failureAnalysis: {
                        ...qualityControl.failureAnalysis,
                        enabled: e.target.checked
                      }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {qualityControl.failureAnalysis.enabled && (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Failure Categories</label>
                    <div className="mt-2 space-y-2">
                      {qualityControl.failureAnalysis.categories.map((category) => (
                        <label key={category} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={true}
                            className="rounded border-gray-300 text-[#FF5722] focus:ring-[#FF5722]"
                          />
                          <span className="ml-2 text-sm text-gray-700">{category}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={qualityControl.failureAnalysis.autoRemediation}
                      onChange={(e) => setQualityControl({
                        ...qualityControl,
                        failureAnalysis: {
                          ...qualityControl.failureAnalysis,
                          autoRemediation: e.target.checked
                        }
                      })}
                      className="rounded border-gray-300 text-[#FF5722] focus:ring-[#FF5722]"
                    />
                    <label className="ml-2 text-sm text-gray-700">Enable Auto-Remediation</label>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Section 9: Auto-Optimization */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-medium mb-4">Auto-Optimization</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* AI-Based Routing */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium">AI-Based Routing</h3>
                  <p className="text-sm text-gray-600">Machine learning optimization for route selection</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoOptimization.aiBasedRouting.enabled}
                    onChange={(e) => setAutoOptimization({
                      ...autoOptimization,
                      aiBasedRouting: {
                        ...autoOptimization.aiBasedRouting,
                        enabled: e.target.checked
                      }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {autoOptimization.aiBasedRouting.enabled && (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Learning Mode</label>
                    <select
                      value={autoOptimization.aiBasedRouting.learningMode}
                      onChange={(e) => setAutoOptimization({
                        ...autoOptimization,
                        aiBasedRouting: {
                          ...autoOptimization.aiBasedRouting,
                          learningMode: e.target.value
                        }
                      })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                    >
                      <option value="continuous">Continuous Learning</option>
                      <option value="scheduled">Scheduled Updates</option>
                      <option value="threshold">Threshold-based</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Optimization Factors</label>
                    <div className="mt-2 space-y-2">
                      {['Delivery Rate', 'Latency', 'Cost', 'Network Quality', 'Historical Performance'].map((factor) => (
                        <label key={factor} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={autoOptimization.aiBasedRouting.optimizationFactors.includes(factor.toLowerCase().replace(' ', '-'))}
                            onChange={(e) => {
                              const factors = e.target.checked
                                ? [...autoOptimization.aiBasedRouting.optimizationFactors, factor.toLowerCase().replace(' ', '-')]
                                : autoOptimization.aiBasedRouting.optimizationFactors.filter(f => f !== factor.toLowerCase().replace(' ', '-'));
                              setAutoOptimization({
                                ...autoOptimization,
                                aiBasedRouting: {
                                  ...autoOptimization.aiBasedRouting,
                                  optimizationFactors: factors
                                }
                              });
                            }}
                            className="rounded border-gray-300 text-[#FF5722] focus:ring-[#FF5722]"
                          />
                          <span className="ml-2 text-sm text-gray-700">{factor}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Update Frequency (hours)</label>
                    <input
                      type="number"
                      min="1"
                      max="72"
                      value={autoOptimization.aiBasedRouting.updateFrequency}
                      onChange={(e) => setAutoOptimization({
                        ...autoOptimization,
                        aiBasedRouting: {
                          ...autoOptimization.aiBasedRouting,
                          updateFrequency: parseInt(e.target.value)
                        }
                      })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Auto-Switching */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium">Auto-Switching</h3>
                  <p className="text-sm text-gray-600">Automatic route failover and recovery</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoOptimization.autoSwitching.enabled}
                    onChange={(e) => setAutoOptimization({
                      ...autoOptimization,
                      autoSwitching: {
                        ...autoOptimization.autoSwitching,
                        enabled: e.target.checked
                      }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {autoOptimization.autoSwitching.enabled && (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Performance Threshold (%)</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={autoOptimization.autoSwitching.performanceThreshold}
                      onChange={(e) => setAutoOptimization({
                        ...autoOptimization,
                        autoSwitching: {
                          ...autoOptimization.autoSwitching,
                          performanceThreshold: parseInt(e.target.value)
                        }
                      })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Failure Conditions</label>
                    <div className="mt-2 space-y-2">
                      {['Timeout', 'High Latency', 'Delivery Failure', 'Connection Error'].map((condition) => (
                        <label key={condition} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={autoOptimization.autoSwitching.failureConditions.includes(condition.toLowerCase().replace(' ', '-'))}
                            onChange={(e) => {
                              const conditions = e.target.checked
                                ? [...autoOptimization.autoSwitching.failureConditions, condition.toLowerCase().replace(' ', '-')]
                                : autoOptimization.autoSwitching.failureConditions.filter(c => c !== condition.toLowerCase().replace(' ', '-'));
                              setAutoOptimization({
                                ...autoOptimization,
                                autoSwitching: {
                                  ...autoOptimization.autoSwitching,
                                  failureConditions: conditions
                                }
                              });
                            }}
                            className="rounded border-gray-300 text-[#FF5722] focus:ring-[#FF5722]"
                          />
                          <span className="ml-2 text-sm text-gray-700">{condition}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Recovery Time (minutes)</label>
                    <input
                      type="number"
                      min="1"
                      value={autoOptimization.autoSwitching.recoveryTime}
                      onChange={(e) => setAutoOptimization({
                        ...autoOptimization,
                        autoSwitching: {
                          ...autoOptimization.autoSwitching,
                          recoveryTime: parseInt(e.target.value)
                        }
                      })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Intelligent Load Distribution */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium">Intelligent Load Distribution</h3>
                  <p className="text-sm text-gray-600">Dynamic traffic adjustment based on system health</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoOptimization.intelligentLoadDistribution.enabled}
                    onChange={(e) => setAutoOptimization({
                      ...autoOptimization,
                      intelligentLoadDistribution: {
                        ...autoOptimization.intelligentLoadDistribution,
                        enabled: e.target.checked
                      }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {autoOptimization.intelligentLoadDistribution.enabled && (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Capacity Threshold (%)</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={autoOptimization.intelligentLoadDistribution.capacityThreshold}
                      onChange={(e) => setAutoOptimization({
                        ...autoOptimization,
                        intelligentLoadDistribution: {
                          ...autoOptimization.intelligentLoadDistribution,
                          capacityThreshold: parseInt(e.target.value)
                        }
                      })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Health Metrics</label>
                    <div className="mt-2 space-y-2">
                      {['Response Time', 'Success Rate', 'Queue Length', 'CPU Usage', 'Memory Usage'].map((metric) => (
                        <label key={metric} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={autoOptimization.intelligentLoadDistribution.healthMetrics.includes(metric.toLowerCase().replace(' ', '-'))}
                            onChange={(e) => {
                              const metrics = e.target.checked
                                ? [...autoOptimization.intelligentLoadDistribution.healthMetrics, metric.toLowerCase().replace(' ', '-')]
                                : autoOptimization.intelligentLoadDistribution.healthMetrics.filter(m => m !== metric.toLowerCase().replace(' ', '-'));
                              setAutoOptimization({
                                ...autoOptimization,
                                intelligentLoadDistribution: {
                                  ...autoOptimization.intelligentLoadDistribution,
                                  healthMetrics: metrics
                                }
                              });
                            }}
                            className="rounded border-gray-300 text-[#FF5722] focus:ring-[#FF5722]"
                          />
                          <span className="ml-2 text-sm text-gray-700">{metric}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Adjustment Interval (minutes)</label>
                    <input
                      type="number"
                      min="1"
                      value={autoOptimization.intelligentLoadDistribution.adjustmentInterval}
                      onChange={(e) => setAutoOptimization({
                        ...autoOptimization,
                        intelligentLoadDistribution: {
                          ...autoOptimization.intelligentLoadDistribution,
                          adjustmentInterval: parseInt(e.target.value)
                        }
                      })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Section 10: API Integration */}
        <div className="bg-white rounded-lg shadow-md p-6 mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium">API Integration</h2>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={apiIntegration.enabled}
                onChange={(e) => setApiIntegration({
                  ...apiIntegration,
                  enabled: e.target.checked
                })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
            </label>
          </div>

          {apiIntegration.enabled && (
            <div className="space-y-6">
              {/* API-Based Routing */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-medium">API-Based Routing</h3>
                    <p className="text-sm text-gray-600">Configure dynamic routing through API endpoints</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={apiIntegration.apiBasedRouting.enabled}
                      onChange={(e) => setApiIntegration({
                        ...apiIntegration,
                        apiBasedRouting: {
                          ...apiIntegration.apiBasedRouting,
                          enabled: e.target.checked
                        }
                      })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                  </label>
                </div>

                {apiIntegration.apiBasedRouting.enabled && (
                  <div className="space-y-4">
                    {apiIntegration.apiBasedRouting.endpoints.map((endpoint, index) => (
                      <div key={endpoint.id} className="border rounded p-3 space-y-3">
                        <div className="flex justify-between items-center">
                          <input
                            type="text"
                            placeholder="Endpoint Name"
                            value={endpoint.name}
                            onChange={(e) => {
                              const newEndpoints = [...apiIntegration.apiBasedRouting.endpoints];
                              newEndpoints[index] = { ...endpoint, name: e.target.value };
                              setApiIntegration({
                                ...apiIntegration,
                                apiBasedRouting: {
                                  ...apiIntegration.apiBasedRouting,
                                  endpoints: newEndpoints
                                }
                              });
                            }}
                            className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                          />
                          <button
                            onClick={() => {
                              const newEndpoints = apiIntegration.apiBasedRouting.endpoints.filter(e => e.id !== endpoint.id);
                              setApiIntegration({
                                ...apiIntegration,
                                apiBasedRouting: {
                                  ...apiIntegration.apiBasedRouting,
                                  endpoints: newEndpoints
                                }
                              });
                            }}
                            className="ml-2 text-red-600 hover:text-red-800"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <input
                            type="text"
                            placeholder="URL"
                            value={endpoint.url}
                            onChange={(e) => {
                              const newEndpoints = [...apiIntegration.apiBasedRouting.endpoints];
                              newEndpoints[index] = { ...endpoint, url: e.target.value };
                              setApiIntegration({
                                ...apiIntegration,
                                apiBasedRouting: {
                                  ...apiIntegration.apiBasedRouting,
                                  endpoints: newEndpoints
                                }
                              });
                            }}
                            className="rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                          />
                          <select
                            value={endpoint.method}
                            onChange={(e) => {
                              const newEndpoints = [...apiIntegration.apiBasedRouting.endpoints];
                              newEndpoints[index] = { ...endpoint, method: e.target.value };
                              setApiIntegration({
                                ...apiIntegration,
                                apiBasedRouting: {
                                  ...apiIntegration.apiBasedRouting,
                                  endpoints: newEndpoints
                                }
                              });
                            }}
                            className="rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                          >
                            <option value="GET">GET</option>
                            <option value="POST">POST</option>
                            <option value="PUT">PUT</option>
                            <option value="DELETE">DELETE</option>
                          </select>
                        </div>
                      </div>
                    ))}
                    <button
                      onClick={() => {
                        const newEndpoint = {
                          id: Date.now(),
                          name: '',
                          url: '',
                          method: 'POST',
                          authType: 'bearer',
                          headers: {}
                        };
                        setApiIntegration({
                          ...apiIntegration,
                          apiBasedRouting: {
                            ...apiIntegration.apiBasedRouting,
                            endpoints: [...apiIntegration.apiBasedRouting.endpoints, newEndpoint]
                          }
                        });
                      }}
                      className="w-full py-2 px-4 border border-[#FF5722] text-[#FF5722] rounded-md hover:bg-[#FF5722] hover:text-white transition-colors"
                    >
                      Add New Endpoint
                    </button>
                  </div>
                )}
              </div>

              {/* Webhook Support */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-medium">Webhook Support</h3>
                    <p className="text-sm text-gray-600">Configure real-time delivery and status notifications</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={apiIntegration.webhookSupport.enabled}
                      onChange={(e) => setApiIntegration({
                        ...apiIntegration,
                        webhookSupport: {
                          ...apiIntegration.webhookSupport,
                          enabled: e.target.checked
                        }
                      })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                  </label>
                </div>

                {apiIntegration.webhookSupport.enabled && (
                  <div className="space-y-4">
                    <div className="flex space-x-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={apiIntegration.webhookSupport.deliveryUpdates}
                          onChange={(e) => setApiIntegration({
                            ...apiIntegration,
                            webhookSupport: {
                              ...apiIntegration.webhookSupport,
                              deliveryUpdates: e.target.checked
                            }
                          })}
                          className="rounded border-gray-300 text-[#FF5722] focus:ring-[#FF5722]"
                        />
                        <span className="ml-2 text-sm">Delivery Updates</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={apiIntegration.webhookSupport.statusNotifications}
                          onChange={(e) => setApiIntegration({
                            ...apiIntegration,
                            webhookSupport: {
                              ...apiIntegration.webhookSupport,
                              statusNotifications: e.target.checked
                            }
                          })}
                          className="rounded border-gray-300 text-[#FF5722] focus:ring-[#FF5722]"
                        />
                        <span className="ml-2 text-sm">Status Notifications</span>
                      </label>
                    </div>

                    <div className="space-y-3">
                      {apiIntegration.webhookSupport.endpoints.map((endpoint, index) => (
                        <div key={endpoint.id} className="border rounded p-3 space-y-3">
                          <div className="flex justify-between items-center">
                            <input
                              type="text"
                              placeholder="Webhook URL"
                              value={endpoint.url}
                              onChange={(e) => {
                                const newEndpoints = [...apiIntegration.webhookSupport.endpoints];
                                newEndpoints[index] = { ...endpoint, url: e.target.value };
                                setApiIntegration({
                                  ...apiIntegration,
                                  webhookSupport: {
                                    ...apiIntegration.webhookSupport,
                                    endpoints: newEndpoints
                                  }
                                });
                              }}
                              className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                            />
                            <button
                              onClick={() => {
                                const newEndpoints = apiIntegration.webhookSupport.endpoints.filter(e => e.id !== endpoint.id);
                                setApiIntegration({
                                  ...apiIntegration,
                                  webhookSupport: {
                                    ...apiIntegration.webhookSupport,
                                    endpoints: newEndpoints
                                  }
                                });
                              }}
                              className="ml-2 text-red-600 hover:text-red-800"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                      <button
                        onClick={() => {
                          const newEndpoint = {
                            id: Date.now(),
                            url: '',
                            events: []
                          };
                          setApiIntegration({
                            ...apiIntegration,
                            webhookSupport: {
                              ...apiIntegration.webhookSupport,
                              endpoints: [...apiIntegration.webhookSupport.endpoints, newEndpoint]
                            }
                          });
                        }}
                        className="w-full py-2 px-4 border border-[#FF5722] text-[#FF5722] rounded-md hover:bg-[#FF5722] hover:text-white transition-colors"
                      >
                        Add New Webhook
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Gateway and Carrier API */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-medium">Gateway and Carrier API</h3>
                    <p className="text-sm text-gray-600">Integrate with multiple SMS gateways and carriers</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={apiIntegration.gatewayAndCarrierApi.enabled}
                      onChange={(e) => setApiIntegration({
                        ...apiIntegration,
                        gatewayAndCarrierApi: {
                          ...apiIntegration.gatewayAndCarrierApi,
                          enabled: e.target.checked
                        }
                      })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                  </label>
                </div>

                {apiIntegration.gatewayAndCarrierApi.enabled && (
                  <div className="space-y-4">
                    {apiIntegration.gatewayAndCarrierApi.connections.map((connection, index) => (
                      <div key={connection.id} className="border rounded p-3 space-y-3">
                        <div className="flex justify-between items-center">
                          <input
                            type="text"
                            placeholder="Connection Name"
                            value={connection.name}
                            onChange={(e) => {
                              const newConnections = [...apiIntegration.gatewayAndCarrierApi.connections];
                              newConnections[index] = { ...connection, name: e.target.value };
                              setApiIntegration({
                                ...apiIntegration,
                                gatewayAndCarrierApi: {
                                  ...apiIntegration.gatewayAndCarrierApi,
                                  connections: newConnections
                                }
                              });
                            }}
                            className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                          />
                          <button
                            onClick={() => {
                              const newConnections = apiIntegration.gatewayAndCarrierApi.connections.filter(c => c.id !== connection.id);
                              setApiIntegration({
                                ...apiIntegration,
                                gatewayAndCarrierApi: {
                                  ...apiIntegration.gatewayAndCarrierApi,
                                  connections: newConnections
                                }
                              });
                            }}
                            className="ml-2 text-red-600 hover:text-red-800"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <select
                            value={connection.type}
                            onChange={(e) => {
                              const newConnections = [...apiIntegration.gatewayAndCarrierApi.connections];
                              newConnections[index] = { ...connection, type: e.target.value };
                              setApiIntegration({
                                ...apiIntegration,
                                gatewayAndCarrierApi: {
                                  ...apiIntegration.gatewayAndCarrierApi,
                                  connections: newConnections
                                }
                              });
                            }}
                            className="rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                          >
                            <option value="gateway">Gateway</option>
                            <option value="carrier">Carrier</option>
                          </select>
                          <input
                            type="text"
                            placeholder="API Key"
                            value={connection.apiKey}
                            onChange={(e) => {
                              const newConnections = [...apiIntegration.gatewayAndCarrierApi.connections];
                              newConnections[index] = { ...connection, apiKey: e.target.value };
                              setApiIntegration({
                                ...apiIntegration,
                                gatewayAndCarrierApi: {
                                  ...apiIntegration.gatewayAndCarrierApi,
                                  connections: newConnections
                                }
                              });
                            }}
                            className="rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                          />
                        </div>
                        <input
                          type="text"
                          placeholder="Base URL"
                          value={connection.baseUrl}
                          onChange={(e) => {
                            const newConnections = [...apiIntegration.gatewayAndCarrierApi.connections];
                            newConnections[index] = { ...connection, baseUrl: e.target.value };
                            setApiIntegration({
                              ...apiIntegration,
                              gatewayAndCarrierApi: {
                                ...apiIntegration.gatewayAndCarrierApi,
                                connections: newConnections
                              }
                            });
                          }}
                          className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                        />
                      </div>
                    ))}
                    <button
                      onClick={() => {
                        const newConnection = {
                          id: Date.now(),
                          name: '',
                          type: 'gateway',
                          apiKey: '',
                          baseUrl: '',
                          status: 'inactive'
                        };
                        setApiIntegration({
                          ...apiIntegration,
                          gatewayAndCarrierApi: {
                            ...apiIntegration.gatewayAndCarrierApi,
                            connections: [...apiIntegration.gatewayAndCarrierApi.connections, newConnection]
                          }
                        });
                      }}
                      className="w-full py-2 px-4 border border-[#FF5722] text-[#FF5722] rounded-md hover:bg-[#FF5722] hover:text-white transition-colors"
                    >
                      Add New Connection
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Section 10: Traffic and Load Management */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-medium mb-4">Traffic and Load Management</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Load Balancing */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium">Load Balancing</h3>
                  <p className="text-sm text-gray-600">Configure traffic distribution across routes</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={trafficManagement.loadBalancing.enabled}
                    onChange={(e) => setTrafficManagement({
                      ...trafficManagement,
                      loadBalancing: {
                        ...trafficManagement.loadBalancing,
                        enabled: e.target.checked
                      }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {trafficManagement.loadBalancing.enabled && (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Distribution Method</label>
                    <select
                      value={trafficManagement.loadBalancing.method}
                      onChange={(e) => setTrafficManagement({
                        ...trafficManagement,
                        loadBalancing: {
                          ...trafficManagement.loadBalancing,
                          method: e.target.value
                        }
                      })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                    >
                      <option value="round-robin">Round Robin</option>
                      <option value="weighted">Weighted Distribution</option>
                      <option value="least-connections">Least Connections</option>
                      <option value="response-time">Response Time Based</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Load Threshold (%)</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={trafficManagement.loadBalancing.threshold}
                      onChange={(e) => setTrafficManagement({
                        ...trafficManagement,
                        loadBalancing: {
                          ...trafficManagement.loadBalancing,
                          threshold: parseInt(e.target.value)
                        }
                      })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Traffic Shaping */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium">Traffic Shaping</h3>
                  <p className="text-sm text-gray-600">Manage traffic flow during peak hours</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={trafficManagement.trafficShaping.enabled}
                    onChange={(e) => setTrafficManagement({
                      ...trafficManagement,
                      trafficShaping: {
                        ...trafficManagement.trafficShaping,
                        enabled: e.target.checked
                      }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {trafficManagement.trafficShaping.enabled && (
                <div className="space-y-4">
                  {trafficManagement.trafficShaping.peakHours.map((period, index) => (
                    <div key={period.id} className="p-3 bg-gray-50 rounded">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-sm font-medium text-gray-700">Start Time</label>
                          <input
                            type="time"
                            value={period.startTime}
                            onChange={(e) => {
                              const newPeakHours = [...trafficManagement.trafficShaping.peakHours];
                              newPeakHours[index].startTime = e.target.value;
                              setTrafficManagement({
                                ...trafficManagement,
                                trafficShaping: {
                                  ...trafficManagement.trafficShaping,
                                  peakHours: newPeakHours
                                }
                              });
                            }}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-700">End Time</label>
                          <input
                            type="time"
                            value={period.endTime}
                            onChange={(e) => {
                              const newPeakHours = [...trafficManagement.trafficShaping.peakHours];
                              newPeakHours[index].endTime = e.target.value;
                              setTrafficManagement({
                                ...trafficManagement,
                                trafficShaping: {
                                  ...trafficManagement.trafficShaping,
                                  peakHours: newPeakHours
                                }
                              });
                            }}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                          />
                        </div>
                      </div>
                      <div className="mt-3">
                        <label className="text-sm font-medium text-gray-700">Max Throughput (msgs/sec)</label>
                        <input
                          type="number"
                          min="1"
                          value={period.maxThroughput}
                          onChange={(e) => {
                            const newPeakHours = [...trafficManagement.trafficShaping.peakHours];
                            newPeakHours[index].maxThroughput = parseInt(e.target.value);
                            setTrafficManagement({
                              ...trafficManagement,
                              trafficShaping: {
                                ...trafficManagement.trafficShaping,
                                peakHours: newPeakHours
                              }
                            });
                          }}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                        />
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      setTrafficManagement({
                        ...trafficManagement,
                        trafficShaping: {
                          ...trafficManagement.trafficShaping,
                          peakHours: [
                            ...trafficManagement.trafficShaping.peakHours,
                            {
                              id: Date.now(),
                              startTime: '09:00',
                              endTime: '17:00',
                              maxThroughput: 1000
                            }
                          ]
                        }
                      });
                    }}
                    className="w-full flex justify-center items-center px-4 py-2 border border-[#FF5722] text-[#FF5722] rounded-md hover:bg-[#FF5722] hover:text-white transition-colors"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Peak Period
                  </button>
                </div>
              )}
            </div>

            {/* Queue Management */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium">Queue Management</h3>
                  <p className="text-sm text-gray-600">Configure message queuing parameters</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={trafficManagement.queueManagement.enabled}
                    onChange={(e) => setTrafficManagement({
                      ...trafficManagement,
                      queueManagement: {
                        ...trafficManagement.queueManagement,
                        enabled: e.target.checked
                      }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {trafficManagement.queueManagement.enabled && (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Queue Size</label>
                    <input
                      type="number"
                      min="1000"
                      value={trafficManagement.queueManagement.queueSize}
                      onChange={(e) => setTrafficManagement({
                        ...trafficManagement,
                        queueManagement: {
                          ...trafficManagement.queueManagement,
                          queueSize: parseInt(e.target.value)
                        }
                      })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Priority Levels</label>
                    <div className="mt-2 space-y-2">
                      {trafficManagement.queueManagement.priorityLevels.map((level, index) => (
                        <div key={level} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="text-sm">{level}</span>
                          <input
                            type="number"
                            min="1"
                            placeholder="Weight"
                            className="w-24 text-sm border rounded px-2 py-1"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Congestion Control */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium">Congestion Control</h3>
                  <p className="text-sm text-gray-600">Manage system overload scenarios</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={trafficManagement.congestionControl.enabled}
                    onChange={(e) => setTrafficManagement({
                      ...trafficManagement,
                      congestionControl: {
                        ...trafficManagement.congestionControl,
                        enabled: e.target.checked
                      }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                </label>
              </div>
              {trafficManagement.congestionControl.enabled && (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">System Load Threshold (%)</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={trafficManagement.congestionControl.threshold}
                      onChange={(e) => setTrafficManagement({
                        ...trafficManagement,
                        congestionControl: {
                          ...trafficManagement.congestionControl,
                          threshold: parseInt(e.target.value)
                        }
                      })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Action on Threshold Exceeded</label>
                    <select
                      value={trafficManagement.congestionControl.action}
                      onChange={(e) => setTrafficManagement({
                        ...trafficManagement,
                        congestionControl: {
                          ...trafficManagement.congestionControl,
                          action: e.target.value
                        }
                      })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#FF5722] focus:ring-[#FF5722] sm:text-sm"
                    >
                      <option value="throttle">Throttle Traffic</option>
                      <option value="queue">Queue Messages</option>
                      <option value="reject">Reject New Messages</option>
                      <option value="redistribute">Redistribute Load</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoutingParametersPage; 